import * as React from 'react';
import {
  Card,
  CardBody,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
} from '@patternfly/react-core';
import { Table, Thead, Tr, Th, Tbody, ISortBy, SortByDirection, OuterScrollContainer, InnerScrollContainer } from '@patternfly/react-table';
import { ChartDonut } from '@patternfly/react-charts/victory';

export type ReportInfo = {
  reportType?: string;
  reportDescription?: string;
  tenantId?: string;
  tenantName?: string;
  requestedBy?: string;
  collectionDate?: string;
};

export type ReportDonutSlice = {
  x: string;
  y: number;
  color: string;
  legend?: string;
};

const REPORT_INFO_FIELDS: { label: string; key: keyof ReportInfo }[] = [
  { label: 'Report Type', key: 'reportType' },
  { label: 'Description', key: 'reportDescription' },
  { label: 'Tenant ID', key: 'tenantId' },
  { label: 'Tenant Name', key: 'tenantName' },
  { label: 'Requested By', key: 'requestedBy' },
  { label: 'Collection Date', key: 'collectionDate' },
];

export const prioritySlices = (rows: { Priority?: number }[]): ReportDonutSlice[] => [
  { x: 'High', y: rows.filter((row) => (row.Priority || 0) > 8).length, color: 'red', legend: 'High Priority' },
  {
    x: 'Medium',
    y: rows.filter((row) => (row.Priority || 0) <= 8 && (row.Priority || 0) > 5).length,
    color: 'orange',
    legend: 'Medium Priority',
  },
  { x: 'Low', y: rows.filter((row) => (row.Priority || 0) <= 5).length, color: 'green', legend: 'Low Priority' },
];

export const coverageSlices = (
  rows: Record<string, unknown>[],
  field: string,
  product: string
): ReportDonutSlice[] => [
  {
    x: `Assets NOT covered by ${product}`,
    y: rows.filter((row) => row[field] === 'FALSE').length,
    color: 'red',
    legend: `Unprotected Assets (${product})`,
  },
  {
    x: `Assets covered by ${product}`,
    y: rows.filter((row) => row[field] === 'TRUE').length,
    color: 'green',
    legend: `Protected Assets (${product})`,
  },
];

export const ReportDonut: React.FunctionComponent<{
  ariaDesc: string;
  ariaTitle: string;
  name: string;
  title: string;
  subTitle: string;
  slices: ReportDonutSlice[];
}> = ({ ariaDesc, ariaTitle, name, title, subTitle, slices }) => (
  <div className="report-chart">
    <ChartDonut
      ariaDesc={ariaDesc}
      ariaTitle={ariaTitle}
      data={slices.map(({ x, y }) => ({ x, y }))}
      labels={({ datum }) => `${datum.x}: ${datum.y}`}
      style={{
        data: {
          fill: ({ datum }) => slices.find((slice) => slice.x === datum.x)?.color || 'gray',
        },
      }}
      legendData={slices.map((slice) => ({
        name: `${slice.legend ?? slice.x}: ${slice.y}`,
        symbol: { fill: slice.color },
      }))}
      legendOrientation="vertical"
      legendPosition="right"
      name={name}
      padding={{ bottom: 20, left: 50, right: 200, top: 20 }}
      title={title}
      subTitle={subTitle}
      width={450}
      innerRadius={80}
      constrainToVisibleArea
    />
  </div>
);

export const ReportInfoCard: React.FunctionComponent<{ reportInfo: ReportInfo }> = ({ reportInfo }) => (
  <Card>
    <CardBody>
      <DescriptionList isHorizontal isCompact>
        {REPORT_INFO_FIELDS.map(({ label, key }) => (
          <DescriptionListGroup key={key}>
            <DescriptionListTerm>{label}</DescriptionListTerm>
            <DescriptionListDescription>{reportInfo[key]}</DescriptionListDescription>
          </DescriptionListGroup>
        ))}
      </DescriptionList>
    </CardBody>
  </Card>
);

export const ReportDataTable: React.FunctionComponent<{
  ariaLabel: string;
  columns: { name: string; width: string }[];
  sortBy: ISortBy;
  onSort: (event: React.MouseEvent, index: number, direction: SortByDirection) => void;
  children: React.ReactNode;
}> = ({ ariaLabel, columns, sortBy, onSort, children }) => {
  const labeledRows = React.Children.map(children, (row) => {
    if (!React.isValidElement<{ children?: React.ReactNode }>(row)) {
      return row;
    }
    return React.cloneElement(row, {
      children: React.Children.map(row.props.children, (cell, index) => {
        if (!React.isValidElement<{ dataLabel?: string }>(cell) || !columns[index]) {
          return cell;
        }
        return React.cloneElement(cell, {
          dataLabel: cell.props.dataLabel ?? columns[index].name,
        });
      }),
    });
  });

  return (
    <OuterScrollContainer className="report-data-table">
      <InnerScrollContainer>
        <Table variant="compact" isStickyHeader aria-label={ariaLabel} style={{ tableLayout: 'fixed', width: '100%' }}>
          <Thead>
            <Tr>
              {columns.map((column, index) => (
                <Th
                  key={column.name}
                  sort={{ sortBy, onSort, columnIndex: index }}
                  style={{ fontSize: '1.1em', fontWeight: 'bold', width: column.width }}
                  modifier="wrap"
                >
                  {column.name}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>{labeledRows}</Tbody>
        </Table>
      </InnerScrollContainer>
    </OuterScrollContainer>
  );
};
